import { cva, type VariantProps } from "class-variance-authority";

/* Style recipes shared by components. They live apart from the components so that component
   files export only components, which keeps React Fast Refresh working. */

/* Two control heights run through the app: 30px (h-8) in toolbars and next to lists, 34px (h-9)
   in forms. Buttons: sm 28px, md 30px, lg 38px for a page's one main action (sign in). */
export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,transform] duration-150 select-none active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-ink text-ink-inverse shadow-sm hover:bg-ink/88",
        secondary:
          "border border-line bg-surface text-ink shadow-xs hover:border-line-strong hover:bg-surface-subtle",
        ghost: "text-ink-muted hover:bg-surface-hover hover:text-ink",
        danger: "bg-danger-solid text-on-solid shadow-sm hover:bg-danger-solid-hover",
      },
      size: {
        sm: "h-7.5 px-2.5 text-[13.5px] [&_svg]:size-3.5",
        md: "h-8 px-3 text-[14px] [&_svg]:size-4",
        lg: "h-10 px-4 text-[15px] [&_svg]:size-4",
        "icon-sm": "size-7.5 [&_svg]:size-3.5",
        icon: "size-8 [&_svg]:size-4",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export const inputClassName =
  "h-9 w-full rounded-lg border border-line bg-surface px-3 text-[14.5px] text-ink shadow-xs transition-[border-color,box-shadow] outline-none placeholder:text-ink-subtle hover:border-line-strong focus:border-focus focus:ring-3 focus:ring-brand-ring aria-invalid:border-danger aria-invalid:focus:ring-danger/15 disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:text-ink-muted disabled:shadow-none disabled:hover:border-line";

/* Heights for the input-like controls (Select, Combobox, DateField): compact "sm" for property
   rows and toolbars, "md" to line up with Input in forms. */
export type ControlSize = "sm" | "md";
export const controlSizeClassName: Record<ControlSize, string> = {
  sm: "h-8 px-2.5 text-[14px]",
  md: "h-9 px-3 text-[14.5px]",
};

/* Radix dropdown menus, so every menu in the app feels the same. */
export const menuContentClassName =
  "z-70 min-w-48 animate-pop-in rounded-xl border border-line bg-surface p-1 shadow-overlay";

export const menuItemClassName =
  "flex h-8 cursor-pointer items-center gap-2.5 rounded-md px-2 text-[14px] text-ink outline-none select-none data-[highlighted]:bg-surface-hover [&_svg]:size-4 [&_svg]:shrink-0";

/* A small heading inside a menu, above a group of items. */
export const menuLabelClassName = "px-2 pt-2 pb-1 text-[12.5px] font-medium text-ink-subtle";

/* Small labels: statuses, roles, tags. Each tone pairs a soft background with AA-contrast text. */
export const chipVariants = cva(
  "inline-flex max-w-full shrink-0 items-center rounded-full border font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "border-line bg-surface-sunken text-ink-muted",
        success: "border-success/20 bg-success-soft text-success",
        warning: "border-warning/20 bg-warning-soft text-warning",
        danger: "border-danger/20 bg-danger-soft text-danger",
        info: "border-info/20 bg-info-soft text-info",
        brand: "border-brand/25 bg-brand-soft text-brand-strong",
      },
      size: {
        sm: "h-5.5 gap-1 px-2 text-[12px]",
        md: "h-6 gap-1.5 px-2.5 text-[13px]",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export type ChipTone = NonNullable<VariantProps<typeof chipVariants>["tone"]>;

/* The option rows of popover lists (Combobox), matching the dropdown menu items. */
export const listItemClassName =
  "flex min-h-8 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1 text-[14px] text-ink outline-none select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-surface-hover";

/* Lists laid out as tables (team members, clients): a framed panel, a header row that stays in
   view while the list scrolls (under the phone top bar below lg), and rows that light up on
   hover. `overflow-clip` rounds the corners without breaking the sticky header. */
export const tableFrameClassName =
  "overflow-clip rounded-2xl border border-line bg-surface shadow-xs";
export const tableHeadClassName =
  "sticky top-14 z-10 border-b border-line bg-surface-subtle/95 px-4 py-2.5 text-[13px] font-medium text-ink-subtle backdrop-blur-sm lg:top-0";
export const tableRowClassName =
  "min-h-15 px-4 py-3 transition-colors hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-offset-[-2px]";

/* The heading of each section of a side panel (the task panel: subtasks, time, attachments,
   comments), so the sections read with one rhythm. */
export const panelSectionTitleClassName =
  "flex items-center gap-2 text-[14.5px] font-semibold text-ink [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-subtle";

/* Stacking order of everything that floats over the page, lowest first: a side sheet (and its
   scrim), a dialog (which may open from a sheet), then popovers and menus (which may open from
   either). Toasts sit above all of them (Sonner's own layer). */
export const LAYER = {
  sheetScrim: "z-40",
  sheet: "z-50",
  dialog: "z-60",
  popover: "z-70",
} as const;

/** The number behind a layer class, for comparing layers ("z-60" is 60). */
export function layerIndex(layer: (typeof LAYER)[keyof typeof LAYER]): number {
  return Number(layer.slice(2));
}
